import React, { useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { formatDate } from '../contracts/contractDetailUtils';

// Reserved per-item source-attribution key (doc-46). Written by the backend
// (AI attribution on multi-source runs, human stamps on hand-added rows);
// never a data column.
export const SOURCE_KEY = '_source';

// Array-of-objects renderer for structured-data fields (doc-47): a real table
// instead of stacked key/value blocks. Columns are inferred from the data —
// union of item keys in first-seen order — until a schema declares itemFields
// (doc-45), whose keys/labels/types then take precedence. This is a data grid,
// not a nav table: row hover/cursor are suppressed via .sd-array-tbl.
//
// Per-cell edit: each scalar cell is click-to-edit when canEdit (object/array
// values stay read-only per cell — see isCellEditable). Saving splices the
// new value into a COPY of the array and PATCHes the WHOLE array through the
// existing per-field endpoint — server granularity stays per-field, so any
// cell edit marks the field human/locked, exactly like the JSON pencil (which
// remains available for bulk edits).

// Union of item keys in first-seen order; declared itemFields lead and carry
// their label/type (and options for choice columns), undeclared keys found in
// the data are appended after.
export function inferColumns(items, itemFields) {
  const cols = [];
  const seen = new Set();
  for (const f of itemFields || []) {
    if (!f?.key || seen.has(f.key)) continue;
    seen.add(f.key);
    cols.push({
      key: f.key,
      label: f.label || f.key,
      type: f.type || null,
      ...(Array.isArray(f.options) && f.options.length ? { options: f.options } : {}),
    });
  }
  for (const item of items || []) {
    if (typeof item !== 'object' || item === null) continue;
    for (const key of Object.keys(item)) {
      if (key === SOURCE_KEY || seen.has(key)) continue;
      seen.add(key);
      cols.push({ key, label: key, type: null });
    }
  }
  return cols;
}

// Source class of one row for the marker + filter: rows with no _source tag
// came from the contract's own document.
export function rowSourceType(item) {
  const src = item && typeof item === 'object' ? item[SOURCE_KEY] : null;
  return src && typeof src === 'object' && src.type ? src.type : 'agreement';
}

const SOURCE_META = {
  agreement: { label: 'Doc', filterLabel: 'From the contract document', chip: 'neutral' },
  reference: { label: 'Ref', filterLabel: 'From reference documents', chip: 'blue' },
  related: { label: 'Rel', filterLabel: 'From related contracts', chip: 'purple' },
  human: { label: 'Added', filterLabel: 'Added by hand', chip: 'green' },
};

function sourceTitle(item) {
  const src = item && typeof item === 'object' ? item[SOURCE_KEY] : null;
  if (!src || typeof src !== 'object') return 'From the contract document';
  if (src.type === 'reference') return `From reference document: ${src.fileName || 'unknown'}`;
  if (src.type === 'related') return `From related contract: ${src.contractTitle || src.contractId || 'unknown'}${src.fileName ? ` — ${src.fileName}` : ''}`;
  if (src.type === 'human') {
    return `Added by ${src.addedByName || 'unknown'}${src.addedAt ? `, ${formatDate(src.addedAt)}` : ''} — kept on rerun`;
  }
  return 'From the contract document';
}

// Editor kind for a column: a declared itemFields type wins; otherwise the
// first non-null value in the column decides. Everything non-numeric and
// non-boolean edits as text.
export function inferCellKind(items, col) {
  if (col.type === 'number' || col.type === 'boolean' || col.type === 'date' || col.type === 'enum') return col.type;
  if (col.type) return 'text';
  for (const item of items || []) {
    const v = item?.[col.key];
    if (v === null || v === undefined) continue;
    if (typeof v === 'number') return 'number';
    if (typeof v === 'boolean') return 'boolean';
    return 'text';
  }
  return 'text';
}

// Parse edited cell text back to a typed value. Empty input clears the cell
// (null = "not in the document"); a non-numeric number or a malformed date
// throws a user-readable Error so the edit is never silently coerced.
// badInput: the browser sanitizes invalid typed text (e.g. '1e' in a number
// input) to value '' while validity.badInput is true — that must error, not
// take the empty-clears-to-null path and silently discard the cell.
export function parseCellText(kind, text, badInput = false) {
  if (badInput) {
    if (kind === 'number') throw new Error('Enter a number');
    if (kind === 'date') throw new Error('Enter a date (YYYY-MM-DD)');
    throw new Error('Invalid value');
  }
  const t = String(text ?? '').trim();
  if (t === '') return null;
  if (kind === 'number') {
    const n = Number(t.replace(/[$,\s]/g, ''));
    if (!Number.isFinite(n)) throw new Error('Enter a number');
    return n;
  }
  if (kind === 'boolean') return t === 'true';
  if (kind === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) throw new Error('Enter a date (YYYY-MM-DD)');
    return t;
  }
  return t;
}

// Immutable single-cell update: new array, new row object, other rows shared.
export function spliceCell(items, rowIndex, key, value) {
  return items.map((item, i) => (i === rowIndex ? { ...item, [key]: value } : item));
}

// Object/array-valued cells (inferred columns on legacy prose-only schemas)
// have no per-cell editor — the text input would seed String(value) =
// '[object Object]' and save that literal. They stay read-only per cell;
// the JSON pencil edits the whole array.
export function isCellEditable(value) {
  return value === null || value === undefined || typeof value !== 'object';
}

function CellValue({ value, col }) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--text-tertiary)' }} title="Not found in the document">—</span>;
  }
  // Declared column types render with the same idioms StructuredFieldValue
  // uses for top-level fields; undeclared columns fall back to value sniffing.
  if (col?.type === 'date') return <span className="mono">{formatDate(value)}</span>;
  if (col?.type === 'enum') return <span className="chip neutral">{String(value)}</span>;
  if (typeof value === 'number') return <span className="mono">{value.toLocaleString()}</span>;
  if (typeof value === 'boolean') {
    return value ? <span>Yes</span> : <span style={{ color: 'var(--text-secondary)' }}>No</span>;
  }
  const asStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
  // 3-line clamp at a readable measure (doc-112) — cells wrap now instead of
  // single-line ellipsizing; the full value stays in title.
  return (
    <span
      title={asStr}
      style={{
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        maxWidth: 280,
        overflow: 'hidden',
      }}
    >
      {asStr}
    </span>
  );
}

function CellEditor({ kind, col, value, ariaLabel, onSave, onCancel }) {
  const inputRef = useRef(null);
  const [text, setText] = useState(() => {
    if (value === null || value === undefined) return '';
    if (kind === 'boolean') return value ? 'true' : 'false';
    return String(value);
  });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    let parsed;
    try {
      parsed = parseCellText(kind, text, inputRef.current?.validity?.badInput);
    } catch (err) {
      setError(err.message);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(parsed); // parent closes the editor on success
    } catch (err) {
      setError(err?.message || 'Save failed');
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
  };

  const inputProps = {
    ref: inputRef,
    value: text,
    onChange: (e) => setText(e.target.value),
    onKeyDown: handleKeyDown,
    autoFocus: true,
    disabled: saving,
    'aria-label': ariaLabel,
    'aria-invalid': error ? true : undefined,
    style: { minWidth: 90, fontSize: 12, padding: '3px 8px' },
  };

  let input;
  if (kind === 'boolean') {
    input = (
      <select className="select" {...inputProps}>
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  } else if (kind === 'enum') {
    input = (
      <select className="select" {...inputProps}>
        <option value="">—</option>
        {(col?.options || []).map((o) => <option key={o} value={String(o)}>{String(o)}</option>)}
      </select>
    );
  } else {
    input = (
      <input
        className="input"
        type={kind === 'number' ? 'number' : kind === 'date' ? 'date' : 'text'}
        step={kind === 'number' ? 'any' : undefined}
        {...inputProps}
      />
    );
  }

  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
      {input}
      {error && <span style={{ color: 'var(--text-critical)', fontSize: 11 }}>{error}</span>}
    </span>
  );
}

export default function StructuredArrayTable({
  items = [],
  itemFields,
  canEdit = false,
  onSave = null,
  // Row-level handlers (doc-46 mechanism 3). When onRowUpdate is present,
  // per-cell saves go through it (server keeps row provenance straight);
  // otherwise cell edits fall back to PATCHing the whole array via onSave.
  onRowAdd = null,
  onRowUpdate = null,
  onRowDelete = null,
}) {
  const [editing, setEditing] = useState(null); // { row, key } | null
  const [sourceFilter, setSourceFilter] = useState('all');
  const [pendingDelete, setPendingDelete] = useState(null); // row index | null
  const [rowError, setRowError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [canScroll, setCanScroll] = useState({ l: false, r: false });
  const wrapRef = useRef(null);
  const scrollRef = useRef(null);
  const columns = inferColumns(items, itemFields);
  const editable = canEdit && (typeof onSave === 'function' || typeof onRowUpdate === 'function');

  const sourceTypes = new Set(items.map((item) => rowSourceType(item)));
  const hasSourceTags = items.some((item) => item && typeof item === 'object' && item[SOURCE_KEY]);
  const showSourceColumn = hasSourceTags;
  const showFilter = sourceTypes.size > 1;
  const showActionsColumn = editable && !!onRowDelete;

  // Honest scroll affordance (doc-112): can-l/can-r drive the edge fades,
  // directional shadows, and the column-count hint. The fades are inset past
  // the sticky blocks (they mark the SCROLLING region only), and the first
  // data column's sticky offset tracks the measured row-number column.
  useEffect(() => {
    const scroller = scrollRef.current;
    const wrap = wrapRef.current;
    if (!scroller || !wrap) return undefined;
    const update = () => {
      const l = scroller.scrollLeft > 2;
      const r = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 2;
      setCanScroll((prev) => (prev.l === l && prev.r === r ? prev : { l, r }));
      let left = 0;
      let right = 0;
      scroller.querySelectorAll('thead .sticky-l').forEach((el) => { left += el.getBoundingClientRect().width; });
      scroller.querySelectorAll('thead .sticky-r').forEach((el) => { right += el.getBoundingClientRect().width; });
      wrap.style.setProperty('--sd-fade-l', `${left}px`);
      wrap.style.setProperty('--sd-fade-r', `${right}px`);
      const numTh = scroller.querySelector('thead .sd-col-num');
      if (numTh) wrap.style.setProperty('--sd-key-left', `${numTh.getBoundingClientRect().width}px`);
    };
    update();
    scroller.addEventListener('scroll', update, { passive: true });
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(scroller);
      // Content width changes without the scroller box changing (new columns,
      // an editor opening) — watch the table itself too.
      const table = scroller.querySelector('table');
      if (table) observer.observe(table);
    }
    return () => {
      scroller.removeEventListener('scroll', update);
      if (observer) observer.disconnect();
    };
  }, [items, itemFields, showSourceColumn, showActionsColumn]);

  // Filtering keeps ORIGINAL indices — edits and deletes address the stored
  // array, not the filtered view.
  const visible = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => sourceFilter === 'all' || rowSourceType(item) === sourceFilter);

  const saveCell = async (rowIndex, col, parsed) => {
    if (typeof onRowUpdate === 'function') {
      const { [SOURCE_KEY]: _src, ...row } = { ...(items[rowIndex] || {}), [col.key]: parsed };
      await onRowUpdate(rowIndex, row);
    } else {
      await onSave(spliceCell(items, rowIndex, col.key, parsed));
    }
  };

  const runRowAction = async (fn) => {
    setBusy(true);
    setRowError(null);
    try {
      await fn();
    } catch (err) {
      setRowError(err?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const overflowing = canScroll.l || canScroll.r;
  // Source chips stick against the delete column when it renders, else the
  // right edge — a conditional constant (the icon-button column's fixed
  // width), not a measurement.
  const srcRight = showActionsColumn ? 38 : 0;
  const stickyREdge = showActionsColumn ? 'act' : (showSourceColumn ? 'src' : null);

  return (
    <div>
      {(showFilter || overflowing) && (
        <div className="row" style={{ gap: 8, marginBottom: 6, justifyContent: 'space-between' }}>
          {showFilter ? (
            <span className="row" style={{ gap: 8 }}>
              <label className="tertiary" style={{ fontSize: 11 }} htmlFor="sd-source-filter">Source</label>
              <select
                id="sd-source-filter"
                className="select"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                style={{ fontSize: 12, padding: '2px 8px', width: 'auto' }}
              >
                <option value="all">All sources</option>
                {['agreement', 'reference', 'related', 'human'].filter((t) => sourceTypes.has(t)).map((t) => (
                  <option key={t} value={t}>{SOURCE_META[t].filterLabel}</option>
                ))}
              </select>
            </span>
          ) : <span />}
          {overflowing && (
            <span className="sd-colcount">{columns.length} columns · scroll for more →</span>
          )}
        </div>
      )}
      <div ref={wrapRef} className={`sd-tbl-wrap${canScroll.l ? ' can-l' : ''}${canScroll.r ? ' can-r' : ''}`}>
        <div
          ref={scrollRef}
          className="sd-tbl-scroll"
          tabIndex={0}
          aria-label={`${columns.length}-column table — scrolls horizontally`}
        >
          <table className="tbl sd-array-tbl">
            <thead>
              <tr>
                <th className="sticky-l sd-col-num" style={{ width: 1 }} aria-label="Row number" />
                {columns.map((col, colIdx) => (
                  <th key={col.key} className={colIdx === 0 ? 'sticky-l sd-col-key sticky-l-edge' : undefined}>{col.label}</th>
                ))}
                {showSourceColumn && (
                  <th
                    className={`sticky-r sd-col-src${stickyREdge === 'src' ? ' sticky-r-edge' : ''}`}
                    style={{ width: 1, right: srcRight }}
                  >
                    Source
                  </th>
                )}
                {showActionsColumn && <th className="sticky-r sd-col-act sticky-r-edge" style={{ width: 1 }} aria-label="Row actions" />}
              </tr>
            </thead>
            <tbody>
              {visible.map(({ item, index: rowIndex }) => (
                <tr key={rowIndex}>
                  <td className="mono sticky-l sd-col-num" style={{ color: 'var(--text-tertiary)', fontSize: 11, userSelect: 'none' }}>
                    {rowIndex + 1}
                  </td>
                  {columns.map((col, colIdx) => {
                    const cellValue = item?.[col.key];
                    const cellEditable = editable && isCellEditable(cellValue);
                    const isEditing = cellEditable && editing && editing.row === rowIndex && editing.key === col.key;
                    return (
                      <td key={col.key} className={colIdx === 0 ? 'sticky-l sd-col-key sticky-l-edge' : undefined}>
                        {isEditing ? (
                          <CellEditor
                            kind={inferCellKind(items, col)}
                            col={col}
                            value={cellValue}
                            ariaLabel={`Edit ${col.label}, row ${rowIndex + 1}`}
                            onCancel={() => setEditing(null)}
                            onSave={async (parsed) => {
                              await saveCell(rowIndex, col, parsed);
                              setEditing(null);
                            }}
                          />
                        ) : cellEditable ? (
                          <button
                            type="button"
                            className="sd-cell-btn"
                            title="Click to edit"
                            aria-label={`Edit ${col.label}, row ${rowIndex + 1}`}
                            onClick={() => setEditing({ row: rowIndex, key: col.key })}
                          >
                            <CellValue value={cellValue} col={col} />
                          </button>
                        ) : (
                          <CellValue value={cellValue} col={col} />
                        )}
                      </td>
                    );
                  })}
                  {showSourceColumn && (
                    <td
                      className={`sticky-r sd-col-src${stickyREdge === 'src' ? ' sticky-r-edge' : ''}`}
                      style={{ right: srcRight }}
                    >
                      {(() => {
                        const type = rowSourceType(item);
                        const meta = SOURCE_META[type] || SOURCE_META.agreement;
                        return (
                          <span className={`chip sq ${meta.chip}`} title={sourceTitle(item)} style={{ fontSize: 10 }}>
                            {meta.label}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {showActionsColumn && (
                    <td className="sticky-r sd-col-act sticky-r-edge">
                      {pendingDelete === rowIndex ? (
                        <span className="row" style={{ gap: 4, whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            className="btn outline xs"
                            disabled={busy}
                            onClick={() => runRowAction(async () => {
                              await onRowDelete(rowIndex);
                              setPendingDelete(null);
                            })}
                            style={{ color: 'var(--text-critical)' }}
                          >
                            Remove
                          </button>
                          <button type="button" className="btn ghost xs" disabled={busy} onClick={() => setPendingDelete(null)}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="icon-btn xs"
                          title={rowSourceType(item) === 'human'
                            ? 'Remove this hand-added row'
                            : 'Remove this row (a rerun re-reads the sources and may add it back)'}
                          aria-label={`Remove row ${rowIndex + 1}`}
                          onClick={() => setPendingDelete(rowIndex)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {rowError && (
        <div style={{ color: 'var(--text-critical)', fontSize: 11.5, marginTop: 4 }}>{rowError}</div>
      )}
      {editable && onRowAdd && (
        <button
          type="button"
          className="btn ghost xs"
          style={{ marginTop: 6 }}
          disabled={busy}
          title="Add a hand-entered row — kept when the schema reruns"
          onClick={() => runRowAction(() => onRowAdd({}))}
        >
          <Plus className="w-3 h-3" /> Add row
        </button>
      )}
    </div>
  );
}
